import { Card } from "components/Card/Card";
import { useLanguage } from "locales";
import { cdn } from "core/cdn"
import Alert from "components/Alert/Alert";
import { Button } from "components/Button"
import { useNavigate } from "react-router";
import { SURVEY_PAGE } from "core/constants";
import { useDispatch, useSelector } from "react-redux";
import actionsSurvey from "containers/Survey/services/actions";
import { PATH, QUESTIONS } from "containers/Survey/models";
import PreviousButton from "components/Previous/PreviousButton";
import { getSelectedPatientVitals } from "containers/Vitals/services/selector";
import { vitals as vitalList } from "containers/Vitals/services/constants";
import vitalsActions from "containers/Vitals/services/action";
import { getCheckupVitalsToReuse } from "containers/Vitals/utils";
import { getCurrentSelectedPatientId } from "containers/Survey/services/selector";
import { useEffect, useMemo, useState } from "react";
import { GenericModal } from "components/Modal/Modal";
import { ModalCheckup } from "client/documents/utils";
import { actions as documentActions } from "client/documents/actions";
import { useHasCheckupDocument } from "client/documents/checkup";
import { ClientStore, DocumentsStore } from "types/store";
import { DoctorType } from "types/entity";
import {logout} from "containers/Login/services/actions";
import {
    QUESTION_PATIENT,
    QUESTION_RELATIVES,
} from "containers/Survey/services/constants";
import { Diagnosis } from "containers/Diagnosis/Diagnosis";
import { hasBothMediaPermissionsGranted } from "containers/Diagnosis/utils";
import { areConnectedDeviceOff } from "containers/Call/utils";

const checkMediaPermissions = () => {
    return hasBothMediaPermissionsGranted().then((granted) => {
        if (!granted) {
            throw new Error("Camera or microphone permission missing")
        }
        return granted
    })
}

const checkConnectedDeviceOff = () => {
    return areConnectedDeviceOff().then((res) => {
        if (!res) {
            throw new Error("Device still on")
        }
        return res
    })
}

export default function CheckupEnd() {
    const { languages } = useLanguage();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loadingDocument = useSelector(({ documents }: { documents: DocumentsStore }) => documents.loading) as boolean;
    const selectedPatientId = useSelector(getCurrentSelectedPatientId) as number | undefined;
    const selectedPatientVitals = useSelector(getSelectedPatientVitals);
    const [documentVisible, setDocumentVisible] = useState<boolean>(false)
    const relativeId = selectedPatientId !== undefined && selectedPatientId !== 0 ? selectedPatientId : null;
    const [showEndModal, setShowEndModal] = useState<boolean>(false)
    const [showMissingDataModal, setShowMissingDataModal] = useState<boolean>(false)
    const customer = useSelector(
        ({ client }: { client: ClientStore }) => client.customer,
    )
    const relatives = useSelector(
        ({ client }: { client: ClientStore }) => client.relatives || [],
    )
    const [diagnosticLoading, setDiagnosticLoading] = useState<boolean>(false)
    const [diagnosis, setDiagnosis] = useState<boolean>(false)
    const [proceedAfterDiagnostic, setProceedAfterDiagnostic] = useState<(() => void) | null>(null)
    const checkupPayload = useMemo(
        () => ({ relative_id: relativeId, vitals: (selectedPatientVitals ?? {}) as Record<string, unknown> }),
        [relativeId, selectedPatientVitals],
    )
    const hasCheckupDocument = useHasCheckupDocument(checkupPayload)
    const hasVital = (type: string) => !!selectedPatientVitals?.[type]?.value
    // The last weight is preloaded from the patient's account: alone, it fills no card of the report
    const hasCheckupMeasures = (hasVital(vitalList.HEIGHT) && hasVital(vitalList.WEIGHT))
        || hasVital(vitalList.SATURATION)
        || hasVital(vitalList.TENSION)

    const ensureCheckupDocument = () => {
        if (!hasCheckupDocument && !loadingDocument && hasCheckupMeasures) {
            dispatch(documentActions.requestCheckupDocument(checkupPayload))
        }
    }

    // Create the check-up and its report as soon as the patient reaches this
    // screen, so it is listed in their documents whether they print it or not.
    // useHasCheckupDocument keeps this idempotent: coming back to this screen
    // with the same vitals does not create a second check-up.
    useEffect(() => {
        ensureCheckupDocument()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleGenerateCheckupDocument = () => {
        if (!hasCheckupMeasures) {
            setShowMissingDataModal(true)
            return
        }
        setDocumentVisible(true)
    }

    const handleFinish = () => {
        // Retry once if the generation on arrival failed, then let the patient leave
        ensureCheckupDocument()
        setShowEndModal(true)
    }

    const startDiagnostic = (proceed: () => void) => {
        setDiagnosticLoading(true)
        setProceedAfterDiagnostic(() => proceed)
        checkMediaPermissions()
            .then(checkConnectedDeviceOff)
            .then(() => {
                setDiagnosis(false)
                proceed()
            })
            .catch(() => setDiagnosis(true))
            .finally(() => {
                setDiagnosticLoading(false)
            })
    }

    const handleLaunchTeleconsultation = () => {
        startDiagnostic(() => {
            dispatch(actionsSurvey.reset())
            if (selectedPatientId !== undefined) {
                dispatch(
                    vitalsActions.markCheckupVitals(
                        selectedPatientId,
                        getCheckupVitalsToReuse(selectedPatientVitals),
                    ),
                )
            }
            dispatch(actionsSurvey.replaceQuestions(QUESTIONS))
            if (customer) {
                dispatch({ type: QUESTION_PATIENT, payload: customer })
            }
            dispatch({ type: QUESTION_RELATIVES, payload: relatives })
            dispatch(
                actionsSurvey.setStepReplies({
                    id: PATH.doctor_type,
                    replies: [
                        {
                            title: languages.generalistDoctor,
                            value: DoctorType.GENERALIST,
                        },
                    ],
                }),
            )
            navigate(SURVEY_PAGE)
        })
    }

    const handleLogout = () => {
        dispatch(logout())
    }

    if (diagnosis)
        return (
            <Diagnosis
                onStop={() => setDiagnosis(false)}
                onFinish={() => {
                    setDiagnosis(false)
                    proceedAfterDiagnostic?.()
                }}
            />
        )

    return (
        <>
            <PreviousButton
                onClick={() => {
                    navigate(SURVEY_PAGE)
                }}
            />
            <Card
                title={languages.yourCheckupEnded}
                imgUrl={cdn("/images/tlc_terminee_illustration.svg")}
            >
                <div style={{ width: "90%", margin: "0 auto" }}>
                    <Alert type="info"
                        img={cdn("/icons/i_picto_blue.svg")}
                        description={<span dangerouslySetInnerHTML={{ __html: languages.checkupEndedInformation_html }} />}
                        style={{
                            backgroundColor: "#e7f3fc",
                            border: "1px solid rgb(115, 179, 233)",
                            padding: "12px 24px",
                            borderRadius: "6px",
                            color: "rgb(29, 113, 184)",
                            fontSize: "26px",
                            height: "auto",
                            width: "100%",
                        }} />
                    <div style={{ width: "100%", display: "flex", justifyContent: "space-between", gap: 50, paddingTop: 50 }}>
                        <Button
                            onClick={handleFinish}
                            type="default"
                            disabled={loadingDocument}
                            style={{ flex: 3 }}
                        >
                            {languages.finish}
                        </Button>
                        <Button
                            onClick={handleGenerateCheckupDocument}
                            type="primary"
                            loading={loadingDocument}
                            disabled={loadingDocument}
                            style={{ flex: 4 }}
                        >
                            {languages.printMyCheckup}
                        </Button>
                    </div>
                </div>
            </Card>
            <ModalCheckup
                visible={documentVisible}
                title={languages.yourCheckupHealth}
                onAccept={() => {
                    setDocumentVisible(false)
                    setShowEndModal(true)
                }}
                relativeId={relativeId}
                vitals={checkupPayload.vitals}
            />
            <GenericModal
                visible={showMissingDataModal}
                onAccept={() => setShowMissingDataModal(false)}
                acceptText={languages.IGotIt}
                acceptButtonProps={{ danger: false }}
            >
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 40, padding: 35, textAlign: "center" }}>
                    <img src={cdn("/images/picto_warning.svg")} width="100px" alt="" />
                    <span style={{ fontWeight: 500, fontSize: 28 }}>{languages.checkupDocumentMissingData}</span>
                </div>
            </GenericModal>
            <GenericModal
                visible={showEndModal}
                title={languages.anamneseChoiceOfDoctor}
                onCancel={() => handleLaunchTeleconsultation()}
                onAccept={() => handleLogout()}
                acceptText={languages.finishAndDisconnect}
                acceptButtonProps={{ danger: true, type: "primary" }}
                cancelText={languages.launch_tlc}
                cancelButtonProps={{ danger: false, type: "primary", loading: diagnosticLoading, disabled: diagnosticLoading }}
            >
                <div style={{ padding: "0 60px" }}>
                    {languages.checkUpEndComment}
                </div>
            </GenericModal>
        </>
    )
}
